import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { ApiContext } from '@api/api';
import { AsyncJobDetailOutDTO, JobTypeEnum } from '@api/ringil-api.v2.tsx';
import ResponsiveLayout from '@app/layout/ResponsiveLayout.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ExpeditionMultiDuplicationJobDetail from '@components/ringil3/Features/Job/ExpeditionMultiDuplicationJobDetail.tsx';
import MultiduplicationV3JobDetail from '@components/ringil3/Features/Job/MultiduplicationV3/MultiduplicationV3JobDetail.tsx';
import ExportJobDetail from '@components/ringil3/Features/Job/ExportJobDetail.tsx';
import ReservationMultiDuplicationJobDetail from '@components/ringil3/Features/Job/ReservationMultiDuplicationJobDetail.tsx';
import ExpeditionBulkDistributionJobDetail from '@components/ringil3/Features/Job/ExpeditionBulkDistributionJobDetail.tsx';
import LadenFactorJobDetail from '@components/ringil3/Features/Job/LadenFactorJobDetail.tsx';
import SvgLoader from '@app/icons/loader/Loader.tsx';

/**
 * Generic job detail page: resolves the job type and dispatches to the type-specific detail
 * component. Every future job type adds its own component + one line here.
 */
const JobDetailPage: React.FC = () => {
  const { jobId } = useParams();
  const { getAsyncJobDetail } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  // The whole detail is kept, not just the type: the export detail needs exactly this payload, and
  // handing it over saves it re-requesting the identical URL the moment it mounts.
  const [detail, setDetail] = useState<AsyncJobDetailOutDTO>();
  const jobType = detail?.job?.jobType;

  useEffect(() => {
    if (jobId == null) return;
    getAsyncJobDetail(jobId, (d: AsyncJobDetailOutDTO) => setDetail(d));
  }, [jobId]);

  return (
    <ResponsiveLayout title={tr('JobDetail.title', 'Job detail')}>
      {jobType == null && <SvgLoader className={'animate-spin'} />}
      {jobType === JobTypeEnum.EXPEDITION_MULTI_DUPLICATION && <ExpeditionMultiDuplicationJobDetail jobId={jobId} />}
      {jobType === JobTypeEnum.MULTIDUPLICATION_V3 && <MultiduplicationV3JobDetail jobId={jobId} />}
      {jobType === JobTypeEnum.EXPORT && <ExportJobDetail jobId={jobId} initialDetail={detail} />}
      {jobType === JobTypeEnum.RESERVATION_MULTI_DUPLICATION && <ReservationMultiDuplicationJobDetail jobId={jobId} />}
      {jobType === JobTypeEnum.EXPEDITION_BULK_DISTRIBUTION && <ExpeditionBulkDistributionJobDetail jobId={jobId} />}
      {(jobType === JobTypeEnum.LADEN_FACTOR_ACCEPT || jobType === JobTypeEnum.LADEN_FACTOR_PDF_ORDER) && <LadenFactorJobDetail jobId={jobId} />}
    </ResponsiveLayout>
  );
};

export default withAuthenticationRequired(JobDetailPage);
